/**
 * ⓘ — "how does this work?" next to any step or choice that could confuse
 * someone (Corbin, 2026-10-22: "we can't add more mud to the water").
 *
 * One tap opens a short, plain-words explanation and a real screenshot with
 * numbered arrows and labels drawn on it. The office isn't tech-savvy and
 * techs are wrench people: if a screen doesn't make sense, Corbin gets a call.
 * So the words are short, and the picture does the explaining.
 *
 * Usage:
 *   <InfoButton topic={TECH_PLAN_HELP.officeBlock} />
 * Topics live next to the feature they explain (e.g. tech-plan-help.ts).
 */
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface InfoMark {
    /** Where the arrow points, in % of the picture (0–100). */
    x: number;
    y: number;
    /** The label, a few words. */
    label: string;
    /** Which side of the point the label sits on. */
    side?: "left" | "right" | "above" | "below";
}

export interface InfoShot {
    src: string;
    alt: string;
    marks: InfoMark[];
}

export interface InfoTopic {
    /** The dialog title, e.g. "What the tech will do". */
    title: string;
    /** Two to four short sentences. */
    lines: string[];
    shots?: InfoShot[];
}

export function InfoButton({
    topic,
    className,
    tone = "default",
}: {
    topic: InfoTopic;
    className?: string;
    /** "onDark" for the field app's dark header strip. */
    tone?: "default" | "onDark";
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                aria-label={`How this works: ${topic.title}`}
                title="How this works"
                data-testid="info-button"
                className={cn(
                    // 32px visible, 44px hit area via the negative margin pad.
                    "relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-bold leading-none",
                    "before:absolute before:-inset-2 before:content-['']",
                    tone === "onDark"
                        ? "border-white/50 text-white hover:bg-white/15"
                        : "border-sky-600/50 bg-card text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-500/10",
                    className,
                )}
            >
                i
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} title={topic.title} className="max-w-2xl">
                <div className="space-y-4" data-testid="info-dialog">
                    <div className="space-y-2">
                        {topic.lines.map((l, i) => (
                            <p key={i} className="text-[15px] leading-snug">
                                {l}
                            </p>
                        ))}
                    </div>
                    {topic.shots?.map((s) => (
                        <AnnotatedShot key={s.src} shot={s} />
                    ))}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="h-11 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground"
                            onClick={() => setOpen(false)}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

/** A screenshot with numbered red arrows and labels drawn over it. */
export function AnnotatedShot({ shot }: { shot: InfoShot }) {
    return (
        <figure className="space-y-2">
            <div className="relative overflow-hidden rounded-lg border bg-white">
                <img src={shot.src} alt={shot.alt} className="block w-full" loading="lazy" />
                {shot.marks.map((m, i) => (
                    <Mark key={i} n={i + 1} mark={m} />
                ))}
            </div>
            <figcaption className="space-y-1">
                {shot.marks.map((m, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm leading-snug">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold text-white">
                            {i + 1}
                        </span>
                        <span>{m.label}</span>
                    </div>
                ))}
            </figcaption>
        </figure>
    );
}

const ARROW: Record<NonNullable<InfoMark["side"]>, string> = { left: "→", right: "←", above: "↓", below: "↑" };

function Mark({ n, mark }: { n: number; mark: InfoMark }) {
    const side = mark.side ?? "right";
    const pos: React.CSSProperties = { left: `${mark.x}%`, top: `${mark.y}%` };
    const offset =
        side === "right"
            ? "translate-x-2 -translate-y-1/2"
            : side === "left"
              ? "-translate-x-[calc(100%+0.5rem)] -translate-y-1/2"
              : side === "above"
                ? "-translate-x-1/2 -translate-y-[calc(100%+0.5rem)]"
                : "-translate-x-1/2 translate-y-2";
    return (
        <>
            <span
                aria-hidden
                className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-red-600 shadow"
                style={pos}
            />
            <span
                aria-hidden
                className={cn(
                    "pointer-events-none absolute flex items-center gap-1 whitespace-nowrap rounded-md bg-red-600 px-1.5 py-0.5 text-[11px] font-bold text-white shadow",
                    offset,
                )}
                style={pos}
            >
                {side === "left" || side === "above" ? (
                    <>
                        <span>{n}</span>
                        <span>{ARROW[side]}</span>
                    </>
                ) : (
                    <>
                        <span>{ARROW[side]}</span>
                        <span>{n}</span>
                    </>
                )}
            </span>
        </>
    );
}
